import { motion } from 'framer-motion';
import { Calendar, Clock } from '../components/icons';
import { Link } from 'react-router-dom';

const VIMEO_VIDEO_ID = '1201881276';
const VIMEO_HASH = '4203d6a7a3';
const ASSESSMENT_CODE = 'HPOSS3YCPU';

const DAY2_BULLETS = [
  'How to use this model to enter the market differently',
  'How to position yourself beyond generic coaching offers',
  'How to work with clients inside the system',
  'What you can charge — and different ways of charging',
  'How to create recurring income',
  'How to calculate the income you want',
  'How the platform can help generate the actions, messaging, funnel, and playbook to reach that income goal',
];

const HPOS = <>NLP OS<sup className="text-xs align-super">™</sup></>;

export function EventThankYouPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-7"
        >
          <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-3 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            Day 1 Replay
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {HPOS} Foundations Certification
          </h1>
          <p className="text-slate-400 text-sm">
            A link has been sent to your email too — so you can come back anytime.
          </p>
        </motion.div>

        {/* Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-10"
        >
          <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://player.vimeo.com/video/${VIMEO_VIDEO_ID}?h=${VIMEO_HASH}&color=14b8a6&title=0&byline=0&portrait=0&autoplay=1`}
              className="absolute inset-0 w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Day 1 Replay — NLP OS Foundations Certification"
            />
          </div>
        </motion.div>

        {/* What Day 1 covered */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 border border-white/10 rounded-2xl p-7 mb-6"
        >
          <h2 className="text-white font-bold text-lg mb-4">What Day 1 Covered</h2>
          <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
            <p>
              Day 1 laid the foundation for the bigger shift happening in coaching, training, and personal transformation. We explored why the old model of coaching is becoming harder to sustain, why the market is more saturated than ever, and why AI is changing what clients expect from coaches, practitioners, trainers, and change agents.
            </p>
            <p className="text-white font-medium">
              The key message was simple: the future is not about replacing the human coach.
            </p>
            <p>
              It's about upgrading the way we work — moving from isolated coaching sessions into a more complete, human-led, intelligence-based operating system for transformation. That is what {HPOS} is designed to do.
            </p>
          </div>
        </motion.div>

        {/* Prepare for Day 2 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/5 border border-white/10 rounded-2xl p-7 mb-6"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide">
              Coming Up — Day 2
            </span>
          </div>
          <h2 className="text-white font-bold text-xl mb-1">Promote &amp; Profit</h2>
          <p className="text-slate-400 text-sm mb-5">
            This is where the model starts becoming practical as a business.
          </p>

          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Calendar className="w-4 h-4 text-teal-400" />
              <span className="text-white text-sm font-medium">Wednesday, 17th June</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Clock className="w-4 h-4 text-teal-400" />
              <span className="text-white text-sm font-medium">10:00 AM – 1:00 PM EST</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2">
              <Clock className="w-4 h-4 text-teal-400" />
              <span className="text-white text-sm font-medium">3:00 PM – 6:00 PM London</span>
            </div>
          </div>

          <p className="text-slate-300 text-sm mb-4 leading-relaxed">
            We'll look at the new business models for coaches in this new era — including how to use {HPOS} to attract clients, bring them into the system, support them in a more personalized way, and create a more sustainable coaching business.
          </p>
          <p className="text-slate-300 text-sm mb-5 leading-relaxed">
            We'll also explore different ways to work with clients using this system — from high-touch coaching, to lower-touch support, to models where you do the initial work and the platform continues supporting the client between sessions.
          </p>

          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-3">We'll cover:</p>
          <ul className="space-y-2">
            {DAY2_BULLETS.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-slate-300 text-sm">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Complete assessment CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 border border-white/10 rounded-2xl p-7 mb-8"
        >
          <h2 className="text-white font-semibold text-lg mb-3">
            Before Day 2 — Complete Your Assessment
          </h2>
          <p className="text-slate-300 text-sm mb-2 leading-relaxed">
            The assessment is the starting point of the system. Instead of beginning only with <em>"What is the client's problem?"</em>, we begin with a deeper question: <strong className="text-white">Who is this person?</strong>
          </p>
          <p className="text-slate-300 text-sm mb-5 leading-relaxed">
            When you complete your own assessment, Day 2 will make much more sense — you'll be able to connect the model directly to your own profile and insights.
          </p>

          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wide mb-2">Your access code</p>
          <div className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3 mb-5 w-fit">
            <span className="text-white font-mono font-bold text-lg tracking-widest">{ASSESSMENT_CODE}</span>
          </div>

          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-xl text-sm font-semibold transition-colors"
          >
            Complete Your Assessment →
          </Link>
          <p className="text-slate-500 text-xs mt-3">
            Go to the registration page and enter the code above to get started.
          </p>
        </motion.div>

        {/* Presenter */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex items-center justify-center gap-3 mb-8"
        >
          <img
            src="/Patrick_Profile_Under_2MB_q95.jpg"
            alt="Patrick Dahdal"
            className="w-10 h-10 rounded-full object-cover border-2 border-teal-400/50"
          />
          <div>
            <p className="text-white text-sm font-semibold">Patrick Dahdal</p>
            <p className="text-slate-400 text-xs">Founder, NLP OS<sup className="text-xs align-super">™</sup></p>
          </div>
        </motion.div>

        <p className="text-center text-slate-600 text-xs">
          &copy; {new Date().getFullYear()} NLP OS<sup className="text-xs align-super">™</sup> — All rights reserved
        </p>
      </div>
    </div>
  );
}
